import React from "react";
import {
  Box, Container, Heading, Text, SimpleGrid, VStack, HStack,
  Button, Icon, Badge, Divider,
} from "@chakra-ui/react";
import {
  FaMapMarkerAlt, FaArrowRight, FaHome, FaChartLine,
  FaShieldAlt, FaHandshake, FaCheckCircle, FaArrowLeft,
} from "react-icons/fa";
import { Link as RouterLink, useParams, Navigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../../components/SEOHead";
import { cityData, allCities } from "./propertyManagementData";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";
const LIGHT_BG = "#F0F9F7";

const SERVICES = [
  { icon: FaHome, label: "Tenant Acquisition & Screening" },
  { icon: FaShieldAlt, label: "RTA-Compliant Lease Management" },
  { icon: FaChartLine, label: "Proactive Rent Reviews" },
  { icon: FaHandshake, label: "Maintenance Coordination" },
  { icon: FaCheckCircle, label: "Routine Inspections & Reports" },
  { icon: FaChartLine, label: "Owner Portal & Real-Time Statements" },
];

const PropertyManagementLocation: React.FC = () => {
  const { city: citySlug } = useParams<{ city: string }>();
  const city = citySlug ? cityData[citySlug] : undefined;

  if (!city) {
    return <Navigate to="/property/management" replace />;
  }

  const otherCities = allCities.filter((c) => c.slug !== city.slug).slice(0, 4);

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath={`/property/management/${city.slug}`}
        fallbackTitle={`Property Management ${city.name} — One Property Australia`}
        fallbackDescription={`Professional property management in ${city.name}, ${city.region}. ${city.tagline}`}
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage={`url('${city.heroImage}')`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.82)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={7} textAlign="center">
            <HStack>
              <Icon as={FaMapMarkerAlt} color={PROPERTY_PRIMARY} />
              <Text fontSize="sm" fontWeight="700" color="gray.300" textTransform="uppercase" letterSpacing="wider">
                {city.region}
              </Text>
            </HStack>
            <Badge
              bg={PROPERTY_PRIMARY}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Property Management — {city.name}
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="4xl" lineHeight="1.15">
              Property Management in {city.name}
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="3xl" lineHeight="1.8">
              {city.tagline}
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/property/contact"
                size="lg"
                bg={PROPERTY_PRIMARY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                px={8}
                transition="all 0.2s"
              >
                Get a Free Rental Appraisal
              </Button>
              <Button
                as={RouterLink}
                to="/property/management"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                leftIcon={<FaArrowLeft />}
                _hover={{ bg: "whiteAlpha.100" }}
                px={8}
                transition="all 0.2s"
              >
                All Cities
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Market Stats */}
      <Box bg={NAVY} py={10}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            <VStack spacing={1} textAlign="center">
              <Heading size="2xl" color="white">{city.marketStats.avgRentalYield}</Heading>
              <Text color="gray.400" fontSize="sm">Avg Rental Yield</Text>
            </VStack>
            <VStack spacing={1} textAlign="center">
              <Heading size="2xl" color="white">{city.marketStats.vacancyRate}</Heading>
              <Text color="gray.400" fontSize="sm">Vacancy Rate</Text>
            </VStack>
            <VStack spacing={1} textAlign="center">
              <Heading size="2xl" color="white">{city.marketStats.medianRent}</Heading>
              <Text color="gray.400" fontSize="sm">Median Weekly Rent</Text>
            </VStack>
            <VStack spacing={1} textAlign="center">
              <Heading size="2xl" color="white">{city.marketStats.medianHousePrice}</Heading>
              <Text color="gray.400" fontSize="sm">Median House Price</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      <Container maxW="container.xl" py={{ base: 16, md: 20 }}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16}>

          {/* Left: Overview + Insights */}
          <VStack align="flex-start" spacing={10}>
            {/* Market Overview */}
            <Box>
              <Heading size="lg" color={NAVY} mb={5}>
                The {city.name} Rental Market
              </Heading>
              <Text color="gray.700" lineHeight="tall" fontSize="md">
                {city.overview}
              </Text>
            </Box>

            <Divider borderColor="gray.200" />

            {/* Market Insights */}
            <Box w="full">
              <Heading size="md" color={NAVY} mb={6}>
                {city.name} Market Insights
              </Heading>
              <VStack spacing={4} align="stretch">
                {city.marketInsights.map((insight) => (
                  <Box
                    key={insight.title}
                    p={5}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    _hover={{ borderColor: PROPERTY_PRIMARY, shadow: "sm" }}
                    transition="all 0.2s"
                  >
                    <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">
                      {insight.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="tall">
                      {insight.detail}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>

          {/* Right: Suburbs + Services + Why Us */}
          <VStack align="flex-start" spacing={8}>
            {/* Key Suburbs */}
            <Box
              w="full"
              bg={LIGHT_BG}
              p={7}
              borderRadius="2xl"
              border="1px solid"
              borderColor={`${PROPERTY_PRIMARY}30`}
            >
              <HStack mb={5}>
                <Icon as={FaMapMarkerAlt} color={PROPERTY_PRIMARY} boxSize={5} />
                <Heading size="md" color={NAVY}>Suburbs We Manage</Heading>
              </HStack>
              <SimpleGrid columns={2} spacing={2}>
                {city.keySuburbs.map((suburb) => (
                  <HStack key={suburb} spacing={2}>
                    <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} boxSize={3.5} />
                    <Text fontSize="sm" color="gray.700" fontWeight="500">{suburb}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
              <Text fontSize="xs" color="gray.500" mt={4}>
                We manage properties across {city.name} and the broader {city.region} region. Contact us for coverage outside listed suburbs.
              </Text>
            </Box>

            {/* Services */}
            <Box w="full">
              <Heading size="md" color={NAVY} mb={5}>Our Services in {city.name}</Heading>
              <SimpleGrid columns={1} spacing={3}>
                {SERVICES.map((s) => (
                  <HStack
                    key={s.label}
                    p={4}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    spacing={4}
                  >
                    <Icon as={s.icon} color={PROPERTY_PRIMARY} boxSize={5} flexShrink={0} />
                    <Text fontSize="sm" color="gray.700" fontWeight="600">{s.label}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>

            {/* Why Us */}
            <Box
              w="full"
              bg={NAVY}
              p={7}
              borderRadius="2xl"
              color="white"
            >
              <Heading size="md" mb={4}>
                Why Choose Us in {city.name}
              </Heading>
              <Text fontSize="sm" color="gray.300" lineHeight="tall" mb={6}>
                {city.whyUs}
              </Text>
              <Button
                as={RouterLink}
                to="/property/contact"
                bg={PROPERTY_PRIMARY}
                color="white"
                size="md"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                w="full"
                transition="all 0.2s"
              >
                Get a Free Rental Appraisal
              </Button>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* Contact CTA Strip */}
      <Box bg={LIGHT_BG} py={14}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl" color={NAVY}>
              Ready to Maximise Your {city.name} Investment?
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Get a free rental appraisal and find out exactly what your {city.name} property should be earning in today's market.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/property/contact"
                size="lg"
                bg={PROPERTY_PRIMARY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                transition="all 0.2s"
              >
                Free Rental Appraisal
              </Button>
              <Button
                as={RouterLink}
                to="/property/consultation"
                size="lg"
                variant="outline"
                borderColor={PROPERTY_PRIMARY}
                color={PROPERTY_PRIMARY}
                _hover={{ bg: PROPERTY_PRIMARY, color: "white" }}
                transition="all 0.2s"
              >
                Book a Consultation
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Other Cities */}
      <Container maxW="container.xl" py={16}>
        <Heading size="md" color={NAVY} mb={6}>
          Property Management in Other Queensland Cities
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {otherCities.map((c) => (
            <Box
              key={c.slug}
              as={RouterLink}
              to={`/property/management/${c.slug}`}
              p={4}
              bg="gray.50"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              display="block"
              textDecoration="none"
              _hover={{ bg: LIGHT_BG, borderColor: PROPERTY_PRIMARY, textDecoration: "none" }}
              transition="all 0.2s"
            >
              <HStack spacing={2} mb={1}>
                <Icon as={FaMapMarkerAlt} color={PROPERTY_PRIMARY} boxSize={3.5} />
                <Text fontWeight="700" color={NAVY} fontSize="sm">{c.name}</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">{c.region}</Text>
            </Box>
          ))}
          <Box
            as={RouterLink}
            to="/property/management"
            p={4}
            bg={NAVY}
            borderRadius="xl"
            display="block"
            textDecoration="none"
            _hover={{ opacity: 0.9, textDecoration: "none" }}
            transition="all 0.2s"
          >
            <Text fontWeight="700" color="white" fontSize="sm" mb={1}>View All Cities</Text>
            <Text fontSize="xs" color="gray.400">All 12 Queensland locations →</Text>
          </Box>
        </SimpleGrid>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyManagementLocation;
